import React, { useState, useEffect } from 'react';
import {
  Container,
  Form,
  Button,
  Offcanvas,
  Spinner,
  Modal,
} from 'react-bootstrap';
import { FaSyncAlt, FaClock, FaArrowLeft } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import api from '../services/api';

const UserAssetExchange = () => {
  const navigate = useNavigate();
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [fromToken, setFromToken] = useState('USDT');
  const [toToken, setToToken] = useState('ETH');
  const [amount, setAmount] = useState('');
  const [showCoinPicker, setShowCoinPicker] = useState(false);
  const [isSelectingConsume, setIsSelectingConsume] = useState(true);
  const [selectedConsume, setSelectedConsume] = useState('BTC');
  const [selectedReceive, setSelectedReceive] = useState('USDT');
  const [coinList, setCoinList] = useState(['USDT', 'ETH', 'BTC']); // Dynamic list from API
  const [walletData, setWalletData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exchangeLoading, setExchangeLoading] = useState(false);
  const [showResultModal, setShowResultModal] = useState(false);
  const [exchangeResult, setExchangeResult] = useState(null);
  const [exchangeRate, setExchangeRate] = useState(null);

  const swapTokens = () => {
    setFromToken(toToken);
    setToToken(fromToken);
    const temp = selectedConsume;
    setSelectedConsume(selectedReceive);
    setSelectedReceive(temp);
  };

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Fetch wallet balances and available currencies
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch wallet balances
        const walletResponse = await api.get('/api/trading/wallet/balances');
        if (walletResponse.data?.success && walletResponse.data?.data) {
          setWalletData(walletResponse.data.data);
        }

        // Fetch available currencies from prices API
        const pricesResponse = await api.get('/api/trading/prices');
        if (pricesResponse.data?.success && pricesResponse.data?.data) {
          // Check if data is directly an array or nested in prices
          const prices = Array.isArray(pricesResponse.data.data)
            ? pricesResponse.data.data
            : pricesResponse.data.data.prices || [];

          const currencies = prices.map((price) => price.symbol);
          // Remove duplicates and set coin list
          const uniqueCurrencies = [...new Set(currencies)];
          setCoinList(uniqueCurrencies);
        }
      } catch (error) {
        toast.error('Failed to load data', {
          position: 'bottom-left',
          autoClose: 3000,
        });
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // Get balance for a specific currency
  const getBalance = (currency) => {
    if (!walletData) return 0;
    const realBalance = walletData.real?.[currency]?.availableAssets || 0;
    return realBalance;
  };

  // Handle exchange
  const handleExchange = async () => {
    if (!amount || parseFloat(amount) <= 0) {
      toast.error('Please enter a valid amount', {
        position: 'bottom-left',
        autoClose: 3000,
      });
      return;
    }

    const availableBalance = getBalance(selectedConsume);
    if (parseFloat(amount) > availableBalance) {
      toast.error(`Insufficient ${selectedConsume} balance`, {
        position: 'bottom-left',
        autoClose: 3000,
      });
      return;
    }

    setExchangeLoading(true);
    try {
      // Fetch exchange rate first (optional - won't block exchange if it fails)
      try {
        const ratesResponse = await api.get(
          '/api/financial/exchange/rates/live',
        );

        const rates = ratesResponse.data?.data?.rates || [];

        const matchingRate = rates.find(
          (r) => r.from === selectedConsume && r.to === selectedReceive,
        );

        if (matchingRate) {
          setExchangeRate(matchingRate.rate);
        } else {
          setExchangeRate(null);
        }
      } catch (rateError) {
        setExchangeRate(null);
      }

      const response = await api.post('/api/financial/exchange', {
        fromCurrency: selectedConsume,
        toCurrency: selectedReceive,
        amount: parseFloat(amount),
      });

      if (response.data?.success && response.data?.data) {
        setExchangeResult(response.data.data);
        setShowResultModal(true);
        setAmount('');

        // Refresh wallet data
        const walletResponse = await api.get('/api/trading/wallet/balances');
        if (walletResponse.data?.success && walletResponse.data?.data) {
          setWalletData(walletResponse.data.data);
        }

        toast.success('Exchange completed successfully!', {
          position: 'bottom-left',
          autoClose: 3000,
        });
      }
    } catch (error) {
      toast.error(
        error.response?.data?.message || 'Exchange failed. Please try again.',
        {
          position: 'bottom-left',
          autoClose: 3000,
        },
      );
    } finally {
      setExchangeLoading(false);
    }
  };

  return (
    <Container
      fluid
      style={{
        maxWidth: isMobile ? '100%' : '85%',
        padding: 'clamp(1rem, 3vw, 1.5rem)',
        paddingTop: '38px',
        paddingBottom: '100px',
      }}
    >
      {/* Header */}
      <div className='d-flex justify-content-between align-items-center mb-4'>
        <Button
          variant='link'
          className='text-dark p-0'
          onClick={() => navigate(-1)}
        >
          <FaArrowLeft size={20} />
        </Button>
        <h5 className='mb-0'>Exchange</h5>
        <FaClock
          className='text-muted'
          style={{ cursor: 'pointer' }}
          onClick={() => navigate('/exchange-records')}
        />
      </div>

      {/* From Token Section */}
      <div
        className='bg-white rounded shadow-sm p-3 mb-3'
        onClick={() => {
          setIsSelectingConsume(true);
          setShowCoinPicker(true);
        }}
        style={{ cursor: 'pointer' }}
      >
        <div className='d-flex justify-content-between align-items-center mb-1'>
          <strong>Consumption</strong>
          <FaSyncAlt />
        </div>
        <div className='text-muted small mb-2'>Consume balance</div>
        <div className='d-flex justify-content-between'>
          <div>
            <span className='text-success'>●</span> {selectedConsume}
          </div>
          <div className='text-muted small'>
            Transferable balance ({selectedConsume}){' '}
            {loading ? '...' : getBalance(selectedConsume).toFixed(2)}
          </div>
        </div>
      </div>

      {/* Swap Icon */}
      <div className='text-center mb-3'>
        <Button variant='light' className='rounded-circle' onClick={swapTokens}>
          <FaSyncAlt />
        </Button>
      </div>

      {/* To Token Section */}
      <div
        className='bg-white rounded shadow-sm p-3 mb-3'
        onClick={() => {
          setIsSelectingConsume(false);
          setShowCoinPicker(true);
        }}
        style={{ cursor: 'pointer' }}
      >
        <div className='d-flex justify-content-between align-items-center mb-1'>
          <strong>Receive</strong>
        </div>
        <div className='text-muted small mb-2'>
          Receive balance{' '}
          {loading ? '...' : getBalance(selectedReceive).toFixed(2)}
        </div>
        <div className='d-flex justify-content-between'>
          <div>
            <span className='text-success'>●</span> {selectedReceive}
          </div>
          <div className='text-muted small'>
            Transferable balance ({selectedReceive}){' '}
            {loading ? '...' : getBalance(selectedReceive).toFixed(2)}
          </div>
        </div>
      </div>

      {/* Amount Input */}
      <Form.Group className='mb-3'>
        <Form.Label>Amount of exchange</Form.Label>
        <div className='d-flex align-items-center border rounded px-2'>
          <Form.Control
            type='number'
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            placeholder='Amount of exchange'
            className='border-0 shadow-none'
          />
          <Button
            variant='link'
            className='text-decoration-none p-0'
            onClick={() => setAmount(getBalance(selectedConsume).toFixed(2))}
          >
            All
          </Button>
        </div>
      </Form.Group>

      <Button
        variant='success'
        className='w-100 fw-bold'
        onClick={handleExchange}
        disabled={exchangeLoading || loading}
      >
        {exchangeLoading ? (
          <>
            <Spinner
              as='span'
              animation='border'
              size='sm'
              role='status'
              aria-hidden='true'
              className='me-2'
            />
            Exchanging...
          </>
        ) : (
          'Exchange'
        )}
      </Button>

      {/* Coin Selection Popup */}
      <Offcanvas
        show={showCoinPicker}
        onHide={() => setShowCoinPicker(false)}
        placement='bottom'
        style={{ height: '50vh' }}
      >
        <Container
          fluid
          style={{
            maxWidth: isMobile ? '100%' : '85%',
            padding: 'clamp(1rem, 3vw, 1.5rem)',
            backgroundColor: '#FFFFFF',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Offcanvas.Header closeButton>
            <Offcanvas.Title>Select a Coin</Offcanvas.Title>
          </Offcanvas.Header>
          <Offcanvas.Body
            style={{ flex: 1, overflowY: 'auto', paddingBottom: '20px' }}
          >
            {loading ? (
              <div className='text-center py-3'>
                <Spinner animation='border' size='sm' />
                <div className='mt-2 text-muted'>Loading currencies...</div>
              </div>
            ) : coinList.length > 0 ? (
              coinList.map((coin) => (
                <div
                  key={coin}
                  className='border-bottom py-3 d-flex justify-content-between align-items-center'
                  style={{ cursor: 'pointer' }}
                  onClick={() => {
                    if (isSelectingConsume) {
                      setSelectedConsume(coin);
                    } else {
                      setSelectedReceive(coin);
                    }
                    setShowCoinPicker(false);
                  }}
                >
                  <div>
                    <strong>{coin}</strong>
                    <div className='text-muted small'>
                      Balance: {getBalance(coin).toFixed(2)}
                    </div>
                  </div>
                  <div className='text-success'>●</div>
                </div>
              ))
            ) : (
              <div className='text-center text-muted py-3'>
                No currencies available
              </div>
            )}
          </Offcanvas.Body>
        </Container>
      </Offcanvas>

      {/* Exchange Result Modal */}
      <Modal
        show={showResultModal}
        onHide={() => setShowResultModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Exchange Successful</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {exchangeResult && (
            <div>
              <div className='mb-3'>
                <strong>Transaction ID:</strong>
                <div className='text-muted small'>
                  {exchangeResult.transactionId}
                </div>
              </div>
              <div className='mb-3'>
                <strong>Exchange:</strong>
                <div>
                  {exchangeResult.fromAmount} {selectedConsume} →{' '}
                  {exchangeResult.toAmount.toLocaleString()} {selectedReceive}
                </div>
              </div>
              <div className='mb-3'>
                <strong>Exchange Rate:</strong>
                <div>
                  1 {selectedConsume} ={' '}
                  {exchangeRate ? Number(exchangeRate).toFixed(6) : 'N/A'}{' '}
                  {selectedReceive}
                </div>
              </div>
              {/* <div className='mb-3'>
                <strong>Effective Rate:</strong>
                <div>{exchangeResult.effectiveRate.toLocaleString()}</div>
              </div> */}
              <div className='mb-3'>
                <strong>Fee:</strong>
                <div>0.5%</div>
              </div>
              <div className='mb-3'>
                <strong>Status:</strong>
                <div>
                  <span className='badge bg-success'>
                    {exchangeResult.status}
                  </span>
                </div>
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant='primary' onClick={() => setShowResultModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default UserAssetExchange;
